import { computed, nextTick } from 'vue';
import axiosInstance from '@/plugins/axios';
import i18n from '@/plugins/vueI18n';
import { useI18n } from 'vue-i18n';
import { isAxiosError } from 'axios';

/**
 * The function to set the new locale. Use after fetching the locale.
 */
export const setI18nLanguage = async (locale: string) => {
  i18n.global.locale.value = locale;
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   */
  axiosInstance.defaults.headers.common['Accept-Language'] = locale;
  document.querySelector('html')?.setAttribute('lang', locale);
  // TODO: Check if we need the nextTick here
  return nextTick();
};

/**
 * The function for loading locale messages from the backend.
 */
export const loadLocaleMessages = async (locale: string, attempts = 3): Promise<void> => {
  try {
    // Fetch the locale messages from the backend
    const { data } = await axiosInstance.get(`/translations/${locale}/root.webmail`);

    // Set the locale messages in i18n
    i18n.global.setLocaleMessage(locale, data);
    return nextTick(); // Ensure the locale messages are applied after fetching
  } catch (error) {
    if (isAxiosError(error) && error.code === 'ERR_NETWORK' && attempts > 0) {
      return loadLocaleMessages(locale, attempts - 1); // Retry loading if network error
    }

    console.error(`Failed to load locale messages for ${locale}:`, error);
  }
};

/**
 * Change the language, force fetching the locale if not loaded or applying the existing locale.
 */
export const changeLanguage = async (locale: string) => {
  const existingMessages = i18n.global.getLocaleMessage(locale);
  // fetch only when no messages are available
  if (Object.keys(existingMessages).length === 0) {
    await loadLocaleMessages(locale);
  }

  await setI18nLanguage(locale);
};

const useChangeLocale = () => {
  const { t } = useI18n();
  const languageOptions = computed(() => [
    { title: t('settings.general.lang.et'), value: 'et' },
    { title: t('settings.general.lang.en'), value: 'en' },
    { title: t('settings.general.lang.ru'), value: 'ru' },
    { title: t('settings.general.lang.fi'), value: 'fi' }
  ]);

  return { languageOptions };
};

export default useChangeLocale;
